import { Autocomplete, Box, Chip, CircularProgress, TextField } from '@mui/material';
import { createContext, forwardRef, HTMLAttributes, useContext, useEffect, useState } from 'react';
import { useInput } from 'react-admin';
import { useLocation, useNavigate } from 'react-router-dom';
import { CompanySummary, findCompaniesById } from '../../lib/companies';
import { companyLookupOf, useCompanyMatches } from '../../lib/useCompanyMatches';

interface CompanyFilterInputProps {
  source: string;
  label?: string;
  alwaysOn?: boolean;
}

interface CompanyRef {
  id: number;
  uuid: string;
  name: string | null;
}

/** How long a failed lookup of the address's companies waits before it runs again. */
export const NAME_RETRY_MS = 5000;

// What the open list says under its last company, since the field keeps no helper text: it is as tall as the search beside it.
const ListNote = createContext<string | null>(null);

const CompanyListbox = forwardRef<HTMLUListElement, HTMLAttributes<HTMLElement>>(({ children, ...props }, ref) => {
  const note = useContext(ListNote);
  return (
    <ul ref={ref} {...props}>
      {children}
      {note && (
        <Box component="li" role="presentation" sx={{ px: 2, py: 1, color: 'text.secondary', typography: 'caption' }}>
          {note}
        </Box>
      )}
    </ul>
  );
});

// The filter holds company ids; an address from before this input may hold one id as text, or anything typed.
// Only plain digits count: one bad id in the lookup's IN fails it whole, and the server answers that with an empty list.
const idOf = (item: unknown) => {
  const id = typeof item === 'number' ? item : typeof item === 'string' && /^\d+$/.test(item) ? Number(item) : NaN;
  return Number.isSafeInteger(id) && id > 0 ? id : null;
};

const idsOf = (value: unknown): number[] => {
  const values = Array.isArray(value) ? value : value === undefined || value === null || value === '' ? [] : [value];
  return Array.from(new Set(values.map(idOf).filter((id): id is number => id !== null)));
};

const filterOf = (search: string): Record<string, unknown> | null => {
  try {
    const filter = JSON.parse(new URLSearchParams(search).get('filter') ?? 'null');
    return filter && typeof filter === 'object' && !Array.isArray(filter) ? filter : null;
  } catch {
    return null;
  }
};

const holdsExactly = (value: unknown, ids: number[]) => (ids.length === 0 ? value === '' || value === undefined || value === null : Array.isArray(value) && value.length === ids.length && value.every((item, index) => item === ids[index]));

const withId = (company: CompanySummary): company is CompanyRef => company.id !== undefined;

const chipLabel = (company: CompanyRef) => (company.name === null ? String(company.id) : `${company.name || '(no name)'} · ${company.id}`);

/** Companies by id, full uuid or 3 characters of the name; profiles of any picked one are listed. */
const CompanyFilterInput = ({ source, label = 'Company' }: CompanyFilterInputProps) => {
  const { field } = useInput({ source });
  const ids = idsOf(field.value);
  const [input, setInput] = useState('');
  const { matches, total, loading, error, settled } = useCompanyMatches(input, { byId: true });
  const [known, setKnown] = useState<Record<number, CompanyRef>>({});
  const [lookupAttempt, setLookupAttempt] = useState(0);

  // Whatever the address holds that is not an id would match nothing, so only the ids are kept. The address is
  // rewritten in place: going through the filter form would push it, and Back would land on the old one again.
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const filter = filterOf(location.search);
    if (!filter || !(source in filter) || holdsExactly(filter[source], idsOf(filter[source]))) return;
    const { [source]: held, ...rest } = filter;
    const kept = idsOf(held);
    const params = new URLSearchParams(location.search);
    params.set('filter', JSON.stringify(kept.length > 0 ? { ...rest, [source]: kept } : rest));
    navigate({ pathname: location.pathname, search: `?${params}` }, { replace: true, state: location.state });
  }, [location, navigate, source]);

  // Companies named only by the address (a shared link, the back button) are looked up in one request; a failed lookup runs again later.
  const unnamedKey = ids.filter((id) => known[id] === undefined).join(',');
  useEffect(() => {
    if (!unnamedKey) return undefined;
    let cancelled = false;
    let retry: ReturnType<typeof setTimeout> | undefined;
    const wanted = unnamedKey.split(',').map(Number);
    findCompaniesById(wanted)
      .then((found) => {
        if (cancelled) return;
        setKnown((previous) => {
          const next = { ...previous };
          wanted.forEach((id) => {
            next[id] = found.filter(withId).find((company) => company.id === id) ?? { id, uuid: '', name: '(unknown company)' };
          });
          return next;
        });
      })
      .catch(() => {
        if (!cancelled) retry = setTimeout(() => setLookupAttempt((attempt) => attempt + 1), NAME_RETRY_MS);
      });
    return () => {
      cancelled = true;
      if (retry) clearTimeout(retry);
    };
  }, [unnamedKey, lookupAttempt]);

  const selected: CompanyRef[] = ids.map((id) => known[id] ?? { id, uuid: '', name: null });
  // Until the search has caught up with the text, what it found belongs to an older text and is not offered.
  const current = settled && !loading ? matches.filter(withId) : [];
  const options = current.filter((company) => !ids.includes(company.id));
  const note = current.length > 0 && !error && total !== null && total > current.length ? `${current.length} of ${total} companies; type more to narrow` : null;
  const lookup = companyLookupOf(input, true);
  let noOptions = 'Type an ID, a UUID or 3 characters of the name; * skips any text';
  if (error) noOptions = error;
  else if (lookup && (!settled || loading)) noOptions = 'Searching companies…';
  else if (current.length > 0) noOptions = 'Already picked';
  else if (lookup) noOptions = 'No company matches';

  return (
    <ListNote.Provider value={note}>
      <Autocomplete
        multiple
        size="small"
        sx={{
          minWidth: 300,
          // As tall as the search beside it: MUI 5.8 pads a hidden-label autocomplete as if it had a label, later versions do this themselves.
          '& .MuiFilledInput-root.MuiInputBase-hiddenLabel.MuiInputBase-sizeSmall': { pt: 0, pb: 0 },
          '& .MuiFilledInput-root.MuiInputBase-hiddenLabel.MuiInputBase-sizeSmall .MuiAutocomplete-input': { pt: '8px', pb: '9px' }
        }}
        options={options}
        value={selected}
        filterOptions={(all) => all}
        inputValue={input}
        onInputChange={(_, next, reason) => {
          if (reason !== 'reset') setInput(next);
        }}
        onChange={(_, picked) => {
          setKnown((previous) => ({ ...previous, ...Object.fromEntries(picked.filter((company) => company.name !== null).map((company) => [company.id, company])) }));
          field.onChange(picked.length > 0 ? picked.map((company) => company.id) : '');
          setInput('');
        }}
        isOptionEqualToValue={(option, chosen) => option.id === chosen.id}
        getOptionLabel={chipLabel}
        renderOption={(props, company) => (
          <li {...props} key={company.id}>
            {`${chipLabel(company)} · ${company.uuid.slice(0, 8)}`}
          </li>
        )}
        renderTags={(chosen, getTagProps) => chosen.map((company, index) => <Chip {...getTagProps({ index })} key={company.id} size="small" label={chipLabel(company)} title={company.uuid || undefined} />)}
        loading={loading}
        noOptionsText={noOptions}
        ListboxComponent={CompanyListbox}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="filled"
            margin="dense"
            hiddenLabel
            placeholder={ids.length === 0 ? `${label}: ID, UUID or name` : undefined}
            error={Boolean(error)}
            inputProps={{ ...params.inputProps, 'aria-label': label }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={16} aria-label="Searching companies" /> : null}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        )}
      />
    </ListNote.Provider>
  );
};

export default CompanyFilterInput;
